import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Download, Hammer, ShieldAlert, ScrollText, Clock,
    CheckCircle2, AlertCircle, XCircle,
} from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
    CALIF_BADGE, DIM_BADGE, DIMENSIONES_F09, fmtDate, diasRestantes,
} from "./_common";

const DIM_ICON = {
    favorable: CheckCircle2,
    con_observaciones: AlertCircle,
    desfavorable: XCircle,
};

const PLD_COLOR = {
    bajo: "bg-emerald-50 border-emerald-200 text-emerald-800",
    medio: "bg-amber-50 border-amber-200 text-amber-800",
    alto: "bg-red-50 border-red-200 text-red-800",
};

export default function DictamenDetalle() {
    const { folio } = useParams();
    const navigate = useNavigate();
    const [dict, setDict] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/aceptacion/dictamenes/${folio}`);
            setDict(data);
        } catch (err) {
            toast.error("No se pudo cargar el dictamen");
        } finally { setLoading(false); }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folio]);

    if (!dict) {
        return <div className="p-8">{loading ? "Cargando..." : "Dictamen no encontrado"}</div>;
    }

    const diasRest = diasRestantes(dict.fecha_vigencia_hasta);
    const esVigente = dict.estado === "vigente";

    return (
        <div className="p-8 lg:p-10 max-w-[1400px] space-y-6" data-testid="dictamen-detalle-page">
            <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => navigate("/aceptacion/dictamenes")}>
                    <ArrowLeft className="h-4 w-4 mr-1" /> Dictámenes
                </Button>
            </div>

            {/* Hero */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 flex items-start gap-6 flex-wrap">
                <div className="flex-1 min-w-[280px]">
                    <div className="font-mono text-xs text-gray-500 mb-1">{dict.folio}</div>
                    <h1 className="font-display text-3xl text-[color:var(--brand-charcoal)]">
                        Dictamen F-09
                    </h1>
                    <div className="text-sm text-gray-700 mt-2">
                        Solicitud{" "}
                        <Link to={`/aceptacion/solicitudes/${dict.folio_f01 || dict.solicitud_id}`}
                            className="font-mono text-blue-600 hover:underline">
                            {dict.folio_f01 || dict.solicitud_id}
                        </Link>
                        {dict.sesion_comite_id && (
                            <> · Sesión Comité{" "}
                                <Link to="/gobierno/comite/sesiones"
                                    className="font-mono text-blue-600 hover:underline text-xs">
                                    {dict.sesion_comite_id.slice(0, 8)}…
                                </Link>
                            </>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-3 mt-4 text-sm">
                        <div>
                            <div className="text-[11px] uppercase tracking-wider text-gray-500">Fecha emisión</div>
                            <div>{fmtDate(dict.fecha_emision || dict.created_at)}</div>
                        </div>
                        <div className="ml-6">
                            <div className="text-[11px] uppercase tracking-wider text-gray-500">Vigencia hasta</div>
                            <div>{fmtDate(dict.fecha_vigencia_hasta)}</div>
                        </div>
                        <div className="ml-6">
                            <div className="text-[11px] uppercase tracking-wider text-gray-500">Días restantes</div>
                            <div className={`inline-flex items-center gap-1 ${esVigente && diasRest <= 15 ? "text-amber-700 font-semibold" : "text-emerald-700"}`}>
                                <Clock className="h-3 w-3" />
                                {esVigente ? `${diasRest}d` : dict.estado}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Calificación global grande */}
                <div className="text-center">
                    <div className={`inline-flex items-center justify-center h-24 w-24 rounded-full border-4 text-5xl font-display ${CALIF_BADGE[dict.calificacion_global] || "bg-gray-100"}`}>
                        {dict.calificacion_global}
                    </div>
                    <div className="text-xs uppercase tracking-wider text-gray-500 mt-2">Calificación global</div>
                    <div className="text-sm font-semibold mt-1 capitalize">
                        {dict.resolucion?.replace(/_/g, " ")}
                    </div>
                </div>
            </div>

            {/* 6 dimensiones grid */}
            <section>
                <h2 className="font-display text-xl mb-3">Seis dimensiones del análisis</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {DIMENSIONES_F09.map((d) => {
                        const dim = dict.seis_dimensiones?.[d.key];
                        const calif = dim?.calificacion || "con_observaciones";
                        const Icon = DIM_ICON[calif] || AlertCircle;
                        return (
                            <div key={d.key} className={`rounded-lg border p-5 ${DIM_BADGE[calif]}`}
                                data-testid={`dim-${d.key}`}>
                                <div className="flex items-center justify-between gap-2 mb-2">
                                    <h3 className="font-display text-base">{d.label}</h3>
                                    <Icon className="h-5 w-5" />
                                </div>
                                <div className="text-xs uppercase tracking-wider mb-2">
                                    {calif.replace(/_/g, " ")}
                                </div>
                                <div className="text-sm whitespace-pre-wrap">
                                    {dim?.observaciones || <span className="italic opacity-60">Sin observaciones.</span>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Análisis PLD */}
            {dict.analisis_pld && (
                <section className={`rounded-lg border p-6 ${PLD_COLOR[dict.analisis_pld.nivel_riesgo] || "bg-gray-50 border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-3">
                        <ShieldAlert className="h-5 w-5" />
                        <h2 className="font-display text-lg">Análisis PLD · nivel {dict.analisis_pld.nivel_riesgo}</h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-5 text-sm">
                        <div>
                            <div className="text-[11px] uppercase tracking-wider opacity-75 mb-1">
                                Listas consultadas
                            </div>
                            {(dict.analisis_pld.listas_negras_consultadas || []).length > 0 ? (
                                <ul className="list-disc ml-5 space-y-1">
                                    {dict.analisis_pld.listas_negras_consultadas.map((l, i) => <li key={i}>{l}</li>)}
                                </ul>
                            ) : <span className="italic opacity-70">—</span>}
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-wider opacity-75 mb-1">
                                Hallazgos
                            </div>
                            {(dict.analisis_pld.hallazgos || []).length > 0 ? (
                                <ul className="list-disc ml-5 space-y-1">
                                    {dict.analisis_pld.hallazgos.map((h, i) => <li key={i}>{h}</li>)}
                                </ul>
                            ) : <span className="italic opacity-70">Sin hallazgos.</span>}
                        </div>
                        <div className="md:col-span-2">
                            <strong>EDD requerida:</strong>{" "}
                            {dict.analisis_pld.requiere_edd ? "Sí" : "No"}
                        </div>
                    </div>
                </section>
            )}

            {/* Votación */}
            {dict.votacion && (
                <section className="rounded-lg border border-gray-200 bg-white p-6">
                    <div className="flex items-center gap-2 mb-3">
                        <ScrollText className="h-5 w-5" />
                        <h2 className="font-display text-lg">Votación del Comité</h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4 text-sm">
                        <KV k="A favor" v={dict.votacion.a_favor ?? dict.votacion.favor} />
                        <KV k="En contra" v={dict.votacion.en_contra ?? dict.votacion.contra} />
                        <KV k="Abstenciones" v={dict.votacion.abstenciones ?? 0} />
                    </div>
                    {(dict.votacion.detalle || []).length > 0 && (
                        <div className="mt-5">
                            <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">
                                Detalle por miembro
                            </div>
                            <ul className="space-y-1 text-sm">
                                {dict.votacion.detalle.map((v, i) => (
                                    <li key={i} className="flex items-center gap-2 py-1">
                                        <span className={`text-xs px-2 py-0.5 rounded border ${
                                            v.voto === "a_favor" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                                            v.voto === "en_contra" ? "bg-red-50 text-red-700 border-red-200" :
                                            "bg-gray-100 text-gray-600 border-gray-200"
                                        }`}>
                                            {v.voto?.replace(/_/g, " ")}
                                        </span>
                                        <span>{v.miembro || v.email}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </section>
            )}

            {/* Condiciones y motivo */}
            {(dict.condiciones?.length > 0 || dict.motivo_rechazo) && (
                <section className="rounded-lg border border-gray-200 bg-white p-6 space-y-3">
                    {dict.condiciones?.length > 0 && (
                        <div>
                            <h3 className="font-display text-lg mb-2">Condiciones</h3>
                            <ul className="list-disc ml-5 space-y-1 text-sm">
                                {dict.condiciones.map((c, i) => <li key={i}>{c}</li>)}
                            </ul>
                        </div>
                    )}
                    {dict.motivo_rechazo && (
                        <div>
                            <h3 className="font-display text-lg mb-2 text-red-700">Motivo de rechazo</h3>
                            <p className="text-sm text-gray-800">{dict.motivo_rechazo}</p>
                        </div>
                    )}
                </section>
            )}

            {/* Acciones */}
            <div className="flex flex-wrap gap-3 justify-end pt-4 border-t">
                <Button variant="outline"
                    onClick={() => window.open(`${api.defaults.baseURL}/aceptacion/dictamenes/${dict.id}/pdf`, "_blank")}>
                    <Download className="h-4 w-4 mr-2" /> Descargar PDF
                </Button>
                {esVigente && (dict.resolucion === "aprobado" || dict.resolucion === "aprobado_con_condiciones") && (
                    <Button onClick={() => navigate(`/fideicomisos/nuevo?dictamen=${dict.id}`)}
                        data-testid="crear-fideicomiso">
                        <Hammer className="h-4 w-4 mr-2" /> Crear fideicomiso con este dictamen
                    </Button>
                )}
            </div>
        </div>
    );
}

function KV({ k, v }) {
    return (
        <div className="rounded border border-gray-200 p-3">
            <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-0.5">{k}</div>
            <div className="text-lg font-semibold">{v ?? "—"}</div>
        </div>
    );
}
